import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ContractorProfileDTO } from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { formatDateTime } from '../../lib/date';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';
import { Dialog } from '../../components/a11y/Dialog';
import { useAuth } from '../../providers/AuthProvider';

/**
 * Contractors (0.25.0) — the org's maintenance-contractor directory. Add/edit a contractor, invite
 * them to sign in (a 24h magic link to their login address; they land on the mobile job app), and
 * deactivate (suspends the login and signs them out everywhere at once). Assigning jobs happens on
 * the Work orders page.
 */
export const CONTRACTORS_KEY = ['admin', 'contractors'] as const;

function ContractorForm({ existing, onClose }: { existing: ContractorProfileDTO | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [companyName, setCompanyName] = useState(existing?.companyName ?? '');
  const [contactName, setContactName] = useState(existing?.contactName ?? '');
  const [emailsText, setEmailsText] = useState((existing?.emails ?? []).join('\n'));
  const [phone, setPhone] = useState(existing?.phone ?? '');
  const [trade, setTrade] = useState(existing?.trade ?? '');
  const [licenseNo, setLicenseNo] = useState(existing?.licenseNo ?? '');

  const save = useMutation({
    mutationFn: () => {
      const input = {
        companyName: companyName.trim(),
        contactName: contactName.trim() || null,
        emails: emailsText
          .split(/[\n,;]+/)
          .map((e) => e.trim())
          .filter(Boolean),
        phone: phone.trim() || null,
        trade: trade.trim() || null,
        licenseNo: licenseNo.trim() || null,
      };
      return existing ? api.contractors.update(existing.id, input) : api.contractors.create(input);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CONTRACTORS_KEY });
      onClose();
    },
  });

  return (
    <Dialog title={existing ? `Edit ${existing.companyName}` : 'Add contractor'} onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}
        <div>
          <label htmlFor="ctr-company">Company name</label>
          <input id="ctr-company" required maxLength={200} value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
        </div>
        <div>
          <label htmlFor="ctr-contact">Contact name (optional)</label>
          <input id="ctr-contact" maxLength={200} value={contactName} onChange={(e) => setContactName(e.target.value)} />
        </div>
        <div>
          <label htmlFor="ctr-emails">Email addresses (one per line — job emails go to all; the first is the sign-in address)</label>
          <textarea id="ctr-emails" rows={3} value={emailsText} onChange={(e) => setEmailsText(e.target.value)} />
        </div>
        <div>
          <label htmlFor="ctr-phone">Phone (optional)</label>
          <input id="ctr-phone" maxLength={40} value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div>
          <label htmlFor="ctr-trade">Trade (optional)</label>
          <input
            id="ctr-trade"
            maxLength={100}
            value={trade}
            onChange={(e) => setTrade(e.target.value)}
            placeholder="General / Remodel"
          />
        </div>
        <div>
          <label htmlFor="ctr-license">License # (optional)</label>
          <input id="ctr-license" maxLength={60} value={licenseNo} onChange={(e) => setLicenseNo(e.target.value)} />
        </div>
        <div className="row">
          <button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function RowActions({ c, onEdit }: { c: ContractorProfileDTO; onEdit: () => void }) {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const done = () => void qc.invalidateQueries({ queryKey: CONTRACTORS_KEY });
  const invite = useMutation({
    mutationFn: () => api.contractors.invite(c.id),
    onSuccess: (r) => {
      setMsg(`Sign-in link sent to ${r.loginEmail ?? 'the contractor'} (valid 24 hours).`);
      done();
    },
  });
  const toggle = useMutation({
    mutationFn: () => (c.active ? api.contractors.deactivate(c.id) : api.contractors.activate(c.id)),
    onSuccess: done,
  });
  const err = invite.error ?? toggle.error;
  return (
    <div className="stack">
      <div className="row">
        <button type="button" className="secondary" onClick={onEdit}>
          Edit<span className="visually-hidden"> {c.companyName}</span>
        </button>
        {c.active && (
          <button type="button" className="secondary" disabled={invite.isPending || c.emails.length === 0} onClick={() => invite.mutate()}>
            {invite.isPending ? 'Sending…' : c.userId ? 'Resend sign-in link' : 'Invite'}
            <span className="visually-hidden"> {c.companyName}</span>
          </button>
        )}
        <button
          type="button"
          className="secondary"
          disabled={toggle.isPending}
          onClick={() => {
            if (!c.active || window.confirm(`Deactivate ${c.companyName}? They will be signed out immediately.`)) toggle.mutate();
          }}
        >
          {c.active ? 'Deactivate' : 'Reactivate'}
          <span className="visually-hidden"> {c.companyName}</span>
        </button>
      </div>
      {msg && <StatusMessage tone="ok">{msg}</StatusMessage>}
      {err && <StatusMessage tone="err">{errorMessage(err)}</StatusMessage>}
    </div>
  );
}

export function AdminContractors() {
  const query = useQuery({ queryKey: CONTRACTORS_KEY, queryFn: () => api.contractors.list() });
  const [editing, setEditing] = useState<ContractorProfileDTO | 'new' | null>(null);
  // Directory writes (add/edit/invite/deactivate) are OWNER-only (0.25.0); staff see the list.
  const { principal } = useAuth();
  const isOwner = principal?.role === 'owner';

  if (query.isLoading) return <Loading label="Loading contractors…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />;

  const list = query.data.contractors;
  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1>Contractors</h1>
        {isOwner && (
          <button type="button" onClick={() => setEditing('new')}>
            Add contractor
          </button>
        )}
      </div>
      <p className="subtitle">
        Maintenance contractors who can sign in to see and work the jobs you assign them — including the tenant’s
        name and phone for those jobs. Costs they enter are not final until an owner approves them.
        {isOwner ? '' : ' Only an owner can add, invite or deactivate contractors.'}
      </p>

      {list.length === 0 ? (
        <p className="muted">No contractors yet.</p>
      ) : (
        <div className="table-wrap card" style={{ padding: 0 }}>
          <table>
            <caption className="visually-hidden">Contractors</caption>
            <thead>
              <tr>
                <th scope="col">Company</th>
                <th scope="col">Contact</th>
                <th scope="col">Trade</th>
                <th scope="col">Sign-in</th>
                <th scope="col">Open jobs</th>
                {isOwner && <th scope="col">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id}>
                  <td>
                    {c.companyName}
                    {!c.active && <span className="badge" style={{ marginLeft: 6 }}>inactive</span>}
                  </td>
                  <td>
                    {c.contactName ?? '—'}
                    {c.phone ? <div className="muted">{c.phone}</div> : null}
                    {c.emails.length ? <div className="muted">{c.emails.join(', ')}</div> : null}
                  </td>
                  <td>{c.trade ?? '—'}</td>
                  <td>
                    {c.loginStatus
                      ? `${c.loginStatus}${c.lastLoginAt ? ` · last ${formatDateTime(c.lastLoginAt)}` : ' · never signed in'}`
                      : 'not invited'}
                  </td>
                  <td>{c.openJobCount}</td>
                  {isOwner && (
                    <td>
                      <RowActions c={c} onEdit={() => setEditing(c)} />
                      <Link className="button-link" to={`/admin/activity/contractor/${encodeURIComponent(c.id)}`}>
                        Activity<span className="visually-hidden"> for {c.companyName}</span>
                      </Link>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <ContractorForm existing={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}
